import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type SubscriptionTier = "free" | "basic" | "pro" | "premium";

interface SubscriptionState {
  isLoading: boolean;
  subscribed: boolean;
  tier: SubscriptionTier;
  subscriptionEnd: string | null;
  analysisRemaining: number;
}

interface SubscriptionContextType extends SubscriptionState {
  checkSubscription: () => Promise<void>;
  openCheckout: (priceId: string) => Promise<void>;
  openCustomerPortal: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// Stripe price IDs and product mapping
export const SUBSCRIPTION_TIERS = {
  basic: {
    name: "Basic",
    price_id: "price_1SufkUEeOixj58u2IUwTO112",
    product_id: "prod_TsQbUnzlKfY1Um",
    monthly_price: 9.99,
    annual_price: 95.90, // 20% discount
    analyses_limit: 2,
    features: [
      "Core Contract Upload & Analysis",
      "Plain-English Summaries",
      "Basic Red Flag Detection",
      "2 Contract Analysis per Month",
    ],
  },
  pro: {
    name: "Pro",
    price_id: "price_1SufknEeOixj58u2O9KOLCh6",
    product_id: "prod_TsQcLTlrQ7fkaA",
    monthly_price: 19.99,
    annual_price: 191.90,
    analyses_limit: -1, // Unlimited
    features: [
      "Everything in Basic",
      "Unlimited Analysis",
      "Salary Benchmarking",
      "Take-Home Calculator",
      "Cost-of-Life Simulator",
      "Priority Email Support",
    ],
  },
  premium: {
    name: "Premium",
    price_id: "price_1Sufl4EeOixj58u2f8Nra8nj",
    product_id: "prod_TsQc2hdadISvtB",
    monthly_price: 29.99,
    annual_price: 287.90,
    analyses_limit: -1,
    features: [
      "Everything in Pro",
      "Endorsement Shield Suite",
      "Negotiation Coach",
      "Contract Timeline View",
      "Career Dashboard",
      "Community Access",
      "Priority Support",
    ],
  },
} as const;

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SubscriptionState>({
    isLoading: true,
    subscribed: false,
    tier: "free",
    subscriptionEnd: null,
    analysisRemaining: 2,
  });

  const checkSubscription = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setState({
          isLoading: false,
          subscribed: false,
          tier: "free",
          subscriptionEnd: null,
          analysisRemaining: 2,
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke("check-subscription");
      
      if (error) {
        console.error("Error checking subscription:", error);
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      setState({
        isLoading: false,
        subscribed: data.subscribed,
        tier: data.tier as SubscriptionTier,
        subscriptionEnd: data.subscription_end,
        analysisRemaining: data.analysis_remaining,
      });
    } catch (error) {
      console.error("Failed to check subscription:", error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const openCheckout = useCallback(async (priceId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Failed to create checkout session:", error);
      throw error;
    }
  }, []);

  const openCustomerPortal = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Failed to open customer portal:", error);
      throw error;
    }
  }, []);

  useEffect(() => {
    checkSubscription();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkSubscription();
    });

    // Auto-refresh every minute
    const interval = setInterval(checkSubscription, 60000);

    return () => {
      subscription.unsubscribe();
      clearInterval(interval);
    };
  }, [checkSubscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        ...state,
        checkSubscription,
        openCheckout,
        openCustomerPortal,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
}
